import { supabase } from "@/integrations/supabase/client";

interface WeightedAverageResult {
  success: boolean;
  newCostPerUnit: number;
  error?: string;
}

/**
 * Calculates and updates the weighted average cost of an ingredient
 * based on a new stock entry.
 * 
 * Formula: New WAC = (Current Stock × Current Cost + New Qty × New Unit Cost) / (Current Stock + New Qty)
 */
export async function updateWeightedAverageCost(
  ingredientId: string,
  newQuantity: number,
  newUnitCost: number
): Promise<WeightedAverageResult> {
  try {
    // Fetch current ingredient data
    const { data: ingredient, error: fetchError } = await supabase
      .from("ingredients" as any)
      .select("current_stock, cost_per_unit")
      .eq("id", ingredientId)
      .single();

    if (fetchError || !ingredient) {
      return { success: false, newCostPerUnit: 0, error: "Ingrediente não encontrado" };
    }

    const currentStock = (ingredient as any).current_stock || 0;
    const currentCost = (ingredient as any).cost_per_unit || 0;

    // Calculate weighted average cost
    let newCostPerUnit: number;

    if (currentStock <= 0) {
      // If no current stock, use the new unit cost directly
      newCostPerUnit = newUnitCost;
    } else if (newQuantity <= 0) {
      // If no new quantity, keep current cost
      newCostPerUnit = currentCost;
    } else {
      // Calculate weighted average
      const totalValue = (currentStock * currentCost) + (newQuantity * newUnitCost);
      const totalQuantity = currentStock + newQuantity;
      newCostPerUnit = totalValue / totalQuantity;
    }

    // Round to 4 decimal places
    newCostPerUnit = Math.round(newCostPerUnit * 10000) / 10000;

    // Update ingredient with new cost
    const { error: updateError } = await supabase
      .from("ingredients" as any)
      .update({ cost_per_unit: newCostPerUnit })
      .eq("id", ingredientId);

    if (updateError) {
      return { success: false, newCostPerUnit: 0, error: "Erro ao atualizar custo" };
    }

    return { success: true, newCostPerUnit };

  } catch (error) {
    console.error("Error updating weighted average cost:", error);
    return { success: false, newCostPerUnit: 0, error: "Erro interno" };
  }
}

/**
 * Calculates the weighted average cost without updating the database.
 * Useful for preview/display purposes.
 */
export function calculateWeightedAverageCost(
  currentStock: number,
  currentCostPerUnit: number,
  newQuantity: number,
  newUnitCost: number
): number {
  if (currentStock <= 0) {
    return newUnitCost;
  }

  if (newQuantity <= 0) {
    return currentCostPerUnit;
  }

  const totalValue = (currentStock * currentCostPerUnit) + (newQuantity * newUnitCost);
  const totalQuantity = currentStock + newQuantity;
  return Math.round((totalValue / totalQuantity) * 10000) / 10000;
}
